import Filters from "components/pages/questionnaire/filters";
import CurrentFilters from "components/current_filters";
import Question from "components/pages/questionnaire/question";
import Actions from "components/pages/questionnaire/actions";
import OverrideDialog from "components/pages/questionnaire/override_dialog";
import AnswerResultDialog from "components/pages/questionnaire/answer_result_dialog";
import Dialog from "components/dialog";
import TestModeInfo from "components/pages/questionnaire/test_mode_info";
import ShareHandler from "components/share_handler";
import Link from "next/link";

import * as questionnairesApi from "api/questionnaires";
import * as questionnaireAnswersApi from "api/questionnaire_answers";
import * as questionnaireAnswerAuthorizationsApi from "api/questionnaire_answer_authorizations";
import * as legacyQuestionnairesApi from "api/legacy_questionnaires";

import { isBrowser } from "utils/next";
import * as utils from "utils/questionnaire";
import * as lessonsUtils from "utils/lessons";
import { track, increment } from "utils/tracker";
import { normalizeFilters, parentTopic, subTopic } from "utils/metadata";
import clsx from "clsx";
import { BsFilter } from "react-icons/bs";

import { useRouter } from "next/router";
import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useMounted } from "hooks/react";
import { useUser } from "hooks/user";
import { isMobile, resetScroll } from "utils/browser";
import { useNotifications } from "hooks/notifications";
import { useSubscription } from "hooks/subscription";

import { Question as QuestionProps } from "types/question";
import { Topic as TopicProps } from "types/topic";
import { Links } from "types/link";

export interface QuestionnaireComponentProps {
  id: number;
  question: QuestionProps;
  seriousFrom: Date;
  answeredCount: number;
  totalCount: number;
  topics?: TopicProps[];
  totalTime?: number;
  page?: number;
  subject?: TopicProps;
  topic?: TopicProps;
  testDay?: number;
}

export interface PaginationProps {
  prevUrl?: string;
  nextUrl?: string;
  totalCount?: number;
  page?: number;
}

interface QuestionnaireProps {
  question: QuestionProps;
  links?: Links;
  totalCount: number;
  paginationIndex: number;
}

export default function Questionnaire(props: QuestionnaireProps): JSX.Element {
  const { question, links, paginationIndex } = props;

  const { user } = useUser();

  const mounted = useMounted();

  const { isActive: hasActiveSubscription } = useSubscription();
  const [canAnswer, setCanAnswer] = useState(true);

  const initialQuestionnaire = isBrowser()
    ? utils.persistedQuestionnaire()
    : null;
  const [questionnaire, setQuestionnaire] = useState(initialQuestionnaire);
  const [questionnaireAnswer, setQuestionnaireAnswer] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState(
    paginationInfoFromQuestion()
  );
  const [notFound, setNotFound] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [showAnswerResultDialog, setShowAnswerResultDialog] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingActions, setLoadingActions] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [lessonsPath, setLessonsPath] = useState(null);
  const [showOnGoingQuestionnaireButton, setShowOnGoingQuestionnaireButton] =
    useState(false);
  const [searchFilters, setSearchFilters] = useState(null);

  const shouldResetScroll = useRef(false);

  const currentFiltersData = showOnGoingQuestionnaireButton
    ? question
    : questionnaire || question;

  const router = useRouter();
  const { setNotification } = useNotifications();

  function similarQuestionnaireName() {
    if (subTopic(question.topics)) return subTopic(question.topics).name;

    return parentTopic(question.topics).name;
  }

  function similarQuestionnairePath(): string {
    let path = `/questoes/${parentTopic(question.topics).slug}`;
    if (subTopic(question.topics)) {
      path += `/${subTopic(question.topics).slug}`;
    }
    return path;
  }

  function shouldShowSimilarQuestionnaireButton(): boolean {
    return (
      !showOnGoingQuestionnaireButton &&
      !questionnaireAnswer &&
      question.topics.length > 0
    );
  }

  useEffect(() => {
    setQuestionnaireAnswer(null);
    setShowAnswerResultDialog(false);
    if (!question) return;

    setNotFound(false);
    setPaginationInfo(paginationInfoFromQuestion());

    if (questionnaire?.id) return fetchAnswer();

    setLoading(false);
  }, [question]);

  useEffect(() => {
    utils.persistQuestionnaire(questionnaire);
  }, [questionnaire]);

  useEffect(() => {
    if (!user || !questionnaireAnswer) return;

    questionnaireAnswerAuthorizationsApi
      .show(questionnaireAnswer.id)
      .then((response) => {
        setCanAnswer(response.body.data.canAnswer);
      });
  }, [user, questionnaireAnswer]);

  useLayoutEffect(() => {
    if (!shouldResetScroll.current) return;

    function scroll() {
      if (isMobile()) scrollIntoQuestion();
      else resetScroll();
    }

    if (loading) {
      scroll();
    } else {
      shouldResetScroll.current = false;
      scroll();
    }
  }, [loading]);

  function fetchAnswer() {
    setLoading(true);
    setLoadingActions(true);
    questionnaireAnswersApi
      .getByQuestion(questionnaire.id, question)
      .then((response) => {
        const { data, links } = response.body;
        const { answer, totalCount } = data;
        setQuestionnaireAnswer(answer);
        setQuestionnaire({ ...questionnaire, question: answer.question });
        setPaginationInfo({
          ...paginationLinks(links),
          totalCount,
          page: answer.questionnaireNumber
        });

        setLoadingActions(false);
        setLoading(false);
        setShowOnGoingQuestionnaireButton(false);
      })
      .catch((error) => {
        setLoading(false);
        if (error.statusCode !== 404) throw error;

        setLoadingActions(false);
        setShowOnGoingQuestionnaireButton(true);
      });
  }

  function handleAnswerChange(answer: any) {
    if (!hasActiveSubscription && !canAnswer) {
      track("Blocked from answering questionnaire");
      return setShowSubscriptionDialog(true);
    }

    setDisabled(true);
    setQuestionnaireAnswer({ ...questionnaireAnswer, answer });
    const { institution, year } = question;
    const filters = { institution, year };
    const promise = questionnaireAnswer?.id
      ? updateAnswer(answer).then((response) => updateCounts(response))
      : createQuestionnaireFromAnswer(filters, answer);
    promise.then((_response) => setDisabled(false));
  }

  function createQuestionnaireFromAnswer(filters, answer) {
    track("Answered questionnaire", normalizeFilters(filters));
    increment("answered_questionnaires");
    return createQuestionnaire(filters, answer);
  }

  function createQuestionnaire(filters, answer?: string) {
    const answers = answer ? { [question.id]: answer } : {};
    return questionnairesApi
      .create({ ...filters, answers })
      .then((response) => {
        const data = response.body.data;
        const answer = data.answers[0];
        setQuestionnaireAnswer(answer);
        setDisabled(false);
        const { id, page } = data;
        const newQuestionnaire = {
          id,
          page,
          question: answer?.question,
          seriousFrom: data.seriousFrom,
          totalTime: data.totalTime,
          ...utils.filtersFromResponse(response),
          ...utils.questionnaireCounters(response, questionnaire)
        };
        setQuestionnaire(newQuestionnaire);
        return response;
      });
  }

  function updateAnswer(answer: string) {
    increment("answered_questions");
    return questionnaireAnswersApi
      .update(questionnaireAnswer.id, { answer })
      .then((response) => {
        if (response.body.answeredQuestions == 1) {
          increment("answered_questionnaires");
          track("Answered questionnaire", normalizeFilters(questionnaire));
        }
        return response;
      });
  }

  function cancelTestMode() {
    return legacyQuestionnairesApi
      .update(questionnaire.id, {
        completedAt: new Date().toISOString()
      })
      .then(() => {
        setQuestionnaire((prevQuestionnaire) => {
          return { ...prevQuestionnaire, seriousFrom: null };
        });
      });
  }

  function updateCounts(response) {
    setQuestionnaire({
      ...questionnaire,
      ...utils.questionnaireCounters(response, questionnaire)
    });
  }

  function handleSearch(filters) {
    if (!hasActiveSubscription && !canAnswer) {
      track("Blocked from answering questionnaire");
      setShowSubscriptionDialog(true);
      return;
    }

    const answeredQuestionnaire = (questionnaire?.answeredCount || 0) > 0;
    if (!answeredQuestionnaire) return searchQuestions(filters);

    setSearchFilters(filters);
    setShowOverrideDialog(true);
  }

  function confirmOverrideQuestionnaire() {
    setShowOverrideDialog(false);
    searchQuestions(searchFilters);
  }

  function searchQuestions(filters) {
    track("Searched questions", {
      ...normalizeFilters(filters),
      questionCount: filters.questionCount,
      maxQuestions: filters.maxQuestions,
      tweakedQuestionsCount: filters.tweakedQuestionsCount || false
    });
    if (isMobile()) setShowFilters(false);
    setLoading(true);
    createQuestionnaire(filters).then((response) => {
      const answer = response.body.data.answers[0];
      if (!answer) {
        setLoading(false);
        setNotFound(true);
        track("Did not find questions", normalizeFilters(filters));
        return;
      }

      router.push(utils.questionPath(answer.question));
    });
  }

  function handlePaginate() {
    shouldResetScroll.current = true;
    setQuestionnaireAnswer(null);
    setLoading(true);
  }

  function scrollIntoQuestion() {
    document.querySelector("#question").scrollIntoView();
  }

  function paginationInfoFromQuestion() {
    if (!question) return { nextUrl: null };

    return {
      ...paginationLinks(links),
      totalCount: props.totalCount,
      page: paginationIndex || question.examNumber
    };
  }

  function paginationLinks({ prev, next }) {
    const prevUrl = utils.questionPathFromApiUri(prev)?.toLowerCase();
    const nextUrl = utils.questionPathFromApiUri(next)?.toLowerCase();
    return {
      prevUrl: mounted && isMobile() ? urlWithAnchor(prevUrl) : prevUrl,
      nextUrl: mounted && isMobile() ? urlWithAnchor(nextUrl) : nextUrl
    };
  }

  function urlWithAnchor(url) {
    if (!url) return;
    if (!isMobile()) return url;

    return url.toLowerCase() + "#question";
  }

  function completeQuestionnaire(origin = "Main action") {
    legacyQuestionnairesApi
      .update(questionnaire.id, {
        completedAt: new Date().toISOString()
      })
      .then(() => {
        track("Completed questionnaire", {
          ...normalizeFilters(questionnaire),
          origin
        });
        increment("completed_questionnaires");
        router.push(`/simulado-finalizado/${questionnaire.id}`);
        utils.persistQuestionnaire(null);
      });
  }

  function confirmAnswer() {
    if (!questionnaireAnswer?.answer) {
      return setNotification({
        title: "A questão está em branco",
        text: "Não é possível confirmar a resposta antes de marcar alguma alternativa"
      });
    }

    setQuestionnaireAnswer((answer) => {
      return { ...answer, confirmedAt: new Date() };
    });

    questionnaireAnswersApi.update(questionnaireAnswer.id, {
      confirmedAt: new Date().toISOString()
    });
    setShowAnswerResultDialog(true);
  }

  function revealAnswer() {
    document
      .querySelector(".alternatives")
      ?.scrollIntoView({ behavior: "smooth" });

    setQuestionnaireAnswer((answer) => {
      return { ...answer, seenAnswer: true };
    });

    questionnaireAnswersApi.update(questionnaireAnswer.id, {
      seenAnswer: true
    });
  }

  function handleLessonsCount(count) {
    setLessonsPath(null);
    if (!count) return;

    const filters = {
      subject: parentTopic(question.topics),
      topic: subTopic(question.topics)
    };
    setLessonsPath(lessonsUtils.lessonsPath(filters));
  }

  function handleSubscriptionDialogConfirm() {
    track("Clicked to subscribe now on questionnaire limitation");
    router.push("/assinatura");
  }

  function goToOngoingQuestionnaire() {
    setLoading(true);
    setShowOnGoingQuestionnaireButton(false);
    router.push(utils.questionPath(questionnaire.question));
  }

  return (
    <div id="questionnaire" className="flex h-full">
      <div
        id="questionnaire-filters"
        className="hidden-xs w-[300px] sticky z-10 top-0 flex-shrink-0 h-screen shadow-md"
      >
        <Filters
          onSearch={handleSearch}
          loading={loading}
          onClose={() => setShowFilters(false)}
        />
      </div>
      {showFilters && (
        <div className="visible-xs fixed z-40 left-0 top-0 w-full h-full">
          <Filters
            onSearch={handleSearch}
            loading={loading}
            onClose={() => setShowFilters(false)}
            isClosable={true}
          />
        </div>
      )}
      <div id="questionnaire-wrapper" className="w-full">
        <div
          className={clsx(
            "mx-auto p-5 w-full max-w-2xl min-h-screen md:min-h-0",
            {
              "pb-[50px] md:pb-[72px]": mounted && !!questionnaire?.seriousFrom
            }
          )}
        >
          {showOnGoingQuestionnaireButton && (
            <button
              className="btn btn-secondary mb-6 w-full"
              onClick={goToOngoingQuestionnaire}
            >
              Voltar para simulado em andamento
            </button>
          )}
          {shouldShowSimilarQuestionnaireButton() && (
            <Link href={similarQuestionnairePath()} passHref>
              <button className="btn btn-secondary mb-6 w-full">
                Resolver simulado de {similarQuestionnaireName()}
              </button>
            </Link>
          )}
          <div className="flex items-start justify-between mb-5 w-full">
            <div>
              <CurrentFilters
                filters={currentFiltersData}
                resourcesName="Questões"
                loading={loading}
                onClick={() => setShowFilters(true)}
              />
              <div
                className="flex items-center mt-2 md:hidden"
                onClick={() => setShowFilters(true)}
              >
                <BsFilter size={16} />
                <span className="ml-1 text-sm font-semibold">
                  Filtrar questões
                </span>
              </div>
            </div>
            <ShareHandler
              origin="questionnaire"
              questionnaire={questionnaire}
            />
          </div>
          {(notFound && (
            <span>
              Não encontramos nenhuma questão com os filtros selecionados :(
            </span>
          )) || (
            <>
              <Question
                key={`${question.id}-${loading}`}
                {...question}
                questionnaire={questionnaire}
                questionnaireAnswer={questionnaireAnswer}
                disabled={disabled || loadingActions}
                loading={loading}
                onAnswerChange={handleAnswerChange}
                onFetchLessonsCount={handleLessonsCount}
              />
              <div className="mt-5 w-full">
                <Actions
                  questionnaire={questionnaire}
                  questionnaireAnswer={questionnaireAnswer}
                  paginationInfo={paginationInfo}
                  loading={loading || loadingActions}
                  disabled={disabled}
                  onPaginate={handlePaginate}
                  onComplete={completeQuestionnaire}
                  onConfirmAnswer={confirmAnswer}
                  onRevealAnswer={revealAnswer}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <TestModeInfo
        questionnaire={questionnaire}
        onComplete={completeQuestionnaire}
        onCancel={cancelTestMode}
        loading={loading}
      />
      {showOverrideDialog && (
        <OverrideDialog
          currentFilters={questionnaire}
          onConfirm={() => confirmOverrideQuestionnaire()}
          onCancel={() => setShowOverrideDialog(false)}
        />
      )}
      {showAnswerResultDialog && (
        <AnswerResultDialog
          question={question}
          questionnaireAnswer={questionnaireAnswer}
          nextQuestionPath={paginationInfo?.nextUrl}
          lessonsPath={lessonsPath}
          onRevealAnswer={revealAnswer}
          onPaginateNext={handlePaginate}
          onClose={() => setShowAnswerResultDialog(false)}
          onComplete={completeQuestionnaire}
        />
      )}
      {showSubscriptionDialog && (
        <Dialog
          id="exceeded-free-questions"
          title="Questões gratuitas esgotadas"
          confirmText="Assinar agora"
          cancelText="Ignorar"
          onConfirm={handleSubscriptionDialogConfirm}
          onCancel={() => setShowSubscriptionDialog(false)}
          noFade={true}
          bottom={true}
        >
          <p className="dialog-text">
            Seu período de testes de 14 dias acabou e você esgotou as 10
            questões diárias gratuitas.
            <br />
            Assine agora por apenas R$19,90/mês e faça simulados ilimitados!
          </p>
        </Dialog>
      )}
    </div>
  );
}
